import { useQuery } from '@tanstack/react-query';
import type { DayHourQuery, VisitsAnalyticsQuery, VisitsEntriesQuery } from '@troia-v3/types';

import { dashboardsAnalyticsApi } from '../services/dashboardsAnalyticsApi';

const VISITS_KEY = 'dashboards-analytics-visits';
const ENTRIES_KEY = 'dashboards-analytics-entries';
const DAY_HOUR_KEY = 'dashboards-analytics-day-hour';

/**
 * `enabled` espelha o que o Zod do backend exige por período — sem isso a
 * primeira renderização de "mês" dispararia um 422 antes de o `PeriodPicker`
 * ter preenchido ano/mês.
 */
const isQueryComplete = (p: VisitsAnalyticsQuery): boolean => {
  if (p.period === 'month') return p.year !== undefined && p.month !== undefined;
  if (p.period === 'year') return p.year !== undefined;
  if (p.period === 'custom') return p.startDate !== undefined && p.endDate !== undefined;
  return true;
};

export const useVisitsAnalytics = (params: VisitsAnalyticsQuery) =>
  useQuery({
    queryKey: [
      VISITS_KEY,
      params.period, params.year, params.month, params.startDate, params.endDate,
      params.unitId, params.unitState, params.channel, params.origin, params.source,
      params.leadOrigin, params.leadStatus,
    ],
    queryFn: () => dashboardsAnalyticsApi.getVisits(params),
    staleTime: 60_000,
    enabled: isQueryComplete(params),
  });

export const useVisitsEntries = (params: VisitsEntriesQuery, enabled = true) =>
  useQuery({
    queryKey: [
      ENTRIES_KEY,
      params.period, params.year, params.month, params.startDate, params.endDate,
      params.unitId, params.unitState, params.channel, params.origin, params.source,
      params.page, params.limit, params.sortOrder,
    ],
    queryFn: () => dashboardsAnalyticsApi.getEntries(params),
    staleTime: 60_000,
    enabled: enabled && isQueryComplete(params),
  });

/**
 * Concentração dia×hora de leads ou vendas.
 *
 * `enabled` é do caller: a métrica `visits` já vem no payload principal, então
 * a tela só chama isto quando o usuário troca o botão — não faz sentido pagar
 * duas agregações de leads em toda visita à página para um gráfico que a
 * maioria nunca troca.
 */
export const useDayHour = (params: DayHourQuery, enabled: boolean) =>
  useQuery({
    queryKey: [
      DAY_HOUR_KEY,
      params.metric,
      params.period, params.year, params.month, params.startDate, params.endDate,
      params.unitId, params.unitState, params.channel, params.origin, params.source,
      params.leadOrigin, params.leadStatus,
    ],
    queryFn: () => dashboardsAnalyticsApi.getDayHour(params),
    staleTime: 60_000,
    enabled: enabled && isQueryComplete(params),
  });
